import ConstraintsConfig from 'config/constraints-config.json';
import { createLogger } from 'shared/logger';

const log = createLogger('PanelSections');

/**
 * Accordion sections of the Vincoli panel and the display-group -> real-group
 * mapping. 13 sections map 1:1 to a real Constraint Document group (section
 * key === group name). The "asset" section ("Strumenti") is a display grouping
 * that spans 4 real groups; `resolveFieldGroup` resolves each of its fields.
 */

export type ConstraintsPanelMode = 'builder' | 'rebalancing' | 'diagnosis';

// Single source of truth for which Vincoli sections render in each mode.
// The menu-button red-dot derivation reuses this so it can't drift from
// what the panel actually shows.
export function visibleConstraintSectionTypes(
  mode: ConstraintsPanelMode,
): string[] {
  return ConstraintsConfig.constraints
    .filter((c) => {
      if (mode === 'builder') return c.type !== 'order' && c.type !== 'liquidity';
      return c.type !== 'esg' && c.type !== 'wrapper'; // wrapper: builder-only (issue #63)
    })
    .map((c) => c.type);
}

/** The non-1:1 section: which real group each "asset"-section field belongs to. */
const ASSET_FIELD_GROUPS: Record<string, string> = {
  asset_count_min: 'asset',
  asset_count_max: 'asset',
  asset_weight_min: 'asset',
  asset_weight_max: 'asset',
  mutual_funds_min: 'instrument_type',
  mutual_funds_max: 'instrument_type',
  etf_min: 'instrument_type',
  etf_max: 'instrument_type',
  bonds_min: 'instrument_type',
  bonds_max: 'instrument_type',
  stocks_min: 'instrument_type',
  stocks_max: 'instrument_type',
  certificates_min: 'instrument_type',
  certificates_max: 'instrument_type',
  core_min: 'core_satellite',
  core_max: 'core_satellite',
  satellite_min: 'core_satellite',
  satellite_max: 'core_satellite',
  investment_bonds_max: 'investment_threshold',
  investment_funds_max: 'investment_threshold',
};

/** The real Document groups a panel section spans. 13 sections own one group;
 *  "asset" ("Strumenti") spans four. */
const SECTION_GROUPS: Record<string, string[]> = {
  asset: ['asset', 'instrument_type', 'core_satellite', 'investment_threshold'],
};

/** Return every real Document group a panel section owns. */
export function sectionGroups(sectionKey: string): string[] {
  return SECTION_GROUPS[sectionKey] ?? [sectionKey];
}

/** Reverse of SECTION_GROUPS: real Document group -> the panel section that owns it. */
const GROUP_TO_PANEL_SECTION: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const [section, groups] of Object.entries(SECTION_GROUPS)) {
    for (const g of groups) map[g] = section;
  }
  return map;
})();

/**
 * Resolve the panel section a real Document group belongs to. Groups not
 * listed in SECTION_GROUPS map 1:1 to a same-named panel section. The
 * non-1:1 case is the "asset" / "Strumenti" section which spans 4 real
 * groups (asset, instrument_type, core_satellite, investment_threshold).
 * Use this to surface a single Vincoli-panel row for delta counts and
 * any other aggregation that should match the user's accordion view.
 */
export function panelSectionForGroup(realGroup: string): string {
  return GROUP_TO_PANEL_SECTION[realGroup] ?? realGroup;
}

/**
 * Resolve the real Constraint Document group a panel field's value lives under.
 * For every section except "asset" the section key IS the group; "asset" is the
 * merged "Strumenti" display section spanning 4 real groups.
 */
export function resolveFieldGroup(sectionKey: string, fieldName: string): string {
  if (sectionKey !== 'asset') {
    return sectionKey;
  }
  const group = ASSET_FIELD_GROUPS[fieldName];
  if (!group) {
    log.warn('resolveFieldGroup: unknown asset-section field, defaulting to "asset"', { fieldName });
    return 'asset';
  }
  return group;
}
